import React, { useRef } from "react";
import {
  Autocomplete,
  Box,
  Grid,
  ListItem,
  Paper,
  styled,
  Skeleton,
  Typography,
  Button,
  Badge,
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import TimeRangeSelector from "../../components/layouts/common/TimeRangeSelector";
import {
  findObjectKeyByValue,
  titleCase,
  formatNumberCustomPattern,
} from "../../utils/helper";
import { REGIONS_TYPE } from "../../utils/constants";
import { StyledSearchBar } from "../../components/inputs/SearchBar";
import AsyncDropDown from "../../components/inputs/AsyncDropDown";
import { getRegionApi } from "../../apis/region.api";
import { getProductApi } from "../../apis/product.api";
import Counts from "./Counts";
import HearingLossChart from "./HearingLossChart";
import { getCampApi, getCampByIdApi } from "../../apis/camp.api";
import ListController from "../beneficiary/ListController";
import { openModal } from "../../store/actions/modalAction";
import {
  HAS_HEARING_LOSS,
  LEVEL_OF_HEARING_LOSS,
} from "../../utils/beneficiary.constant";
import {
  getCityApi,
  getCountryApi,
  getStateApi,
} from "../../apis/CountryStateCity.api";
import CustomInput from "../../components/inputs/CustomInput";
import ButtonComponentsUi from "../../components/button/ButtonComponentsUi";
import { HiOutlineAdjustmentsHorizontal } from "react-icons/hi2";
import DrawerFilter from "../Beneficiary-modal/DrawerFilter";

const FiltersBox = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const PaddingBoxInDesktop = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingLeft: theme.spacing(3),
  },
}));

const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));
const FilterTitleBox = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "wrap",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));
const DashboardListUi = ({ filters, setFilters, list, loading }) => {
  const { user } = useSelector((state) => state);
  const dispatch = useDispatch();
  const value = useRef({
    country: "India",
    state: null,
    district: null,
  });
  const APICall = (type) => {
    switch (type) {
      case REGIONS_TYPE.COUNTRY:
        return {
          api: (para) => getCountryApi({ ...para, isName: true }),
          label: "Select Country",
          fields: "country",
        };
      case REGIONS_TYPE.STATE:
        return {
          api: (para) =>
            getStateApi({
              ...para,
              country: value.current?.country,
              isName: true,
            }),
          label: "Select State",
          fields: "state",
        };
      case REGIONS_TYPE.DISTRICT:
        return {
          api: (para) =>
            getCityApi({ ...para, state: value.current?.state, isName: true }),
          label: "Select City",
          fields: "district",
        };
      default:
        return null;
    }
  };
  const [openFilter, setOpenFilter] = React.useState(false);

  return (
    <DashboardContainer mb={3} id="dashboard-content">
      <Box mt={3}>
        {/* Counts Section */}
        <Paper p={4} component={Box} mt={3}>
          <FilterTitleBox>
            <Typography variant="h6" mt={3}>
              Overall Data
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
              <Badge
                color="error"
                variant="dot"
                invisible={
                  filters.camp || filters.camp_name || filters.user
                    ? false
                    : true
                }
              >
                <Button
                  variant="outlined"
                  startIcon={<HiOutlineAdjustmentsHorizontal />}
                  onClick={() => setOpenFilter(true)}
                >
                  Filters
                </Button>
              </Badge>
            </Box>
          </FilterTitleBox>
          <DrawerFilter
            open={openFilter}
            onClose={(localFilters) => {
              setOpenFilter(false);
              setFilters({ ...filters, ...localFilters });
            }}
            filters={filters}
            setFilters={setFilters}
            onApply={(values) => {
              setFilters({
                ...values,
              });
              setOpenFilter(false);
            }}
            onClear={() => {
              setFilters({
                ...filters,
                camp: "",
                camp_name: "",
                user:"",
              });
            }}
          />
          <Grid container spacing={2}>
            {loading ? (
              [0, 1, 2, 3].map((i) => (
                <Grid item xs={12} md={3} key={i}>
                  <Paper component={Box} p={2}>
                    <Skeleton variant="rounded" height="120px" width="100%" />
                  </Paper>
                </Grid>
              ))
            ) : (
              <Box
                mt={3}
                // mb={3}
                p={2}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(1, 1fr)",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(4, 1fr)",
                  },
                  gap: 2,
                  width: "100%",
                }}
              >
                {Object.entries(list?.hearingLossCounts || {})
                  .sort(([a], [b]) => {
                    // show ALL (8) first
                    if (Number(a) === 8) return -1;
                    if (Number(b) === 8) return 1;

                    return Number(a) - Number(b);
                  })
                  .map(([key, count]) => (
                    <ButtonComponentsUi
                      key={key}
                      width="100%"
                      colorType={false}
                      Title={
                        Number(key) === 8
                          ? "ALL"
                          : findObjectKeyByValue(
                              Number(key),
                              LEVEL_OF_HEARING_LOSS,
                            )
                              ?.replaceAll("_", " ")
                              ?.toUpperCase()
                      }
                      count={count}
                      cursor="default"
                      // onSubmit={() => {
                      //   setFilters({
                      //     ...filters,
                      //     hearingLoss: Number(key),
                      //   });
                      // }}
                    />
                  ))}
              </Box>
            )}
          </Grid>
          {/* Daily Counts */}
          <Typography variant="h6" mt={2} mb={1}>
            Daily Data
          </Typography>
          <Grid container spacing={2}>
            {loading ? (
              [0, 1, 2, 3].map((i) => (
                <Grid item xs={12} md={3} key={i}>
                  <Paper component={Box} p={2}>
                    <Skeleton variant="rounded" height="120px" width="100%" />
                  </Paper>
                </Grid>
              ))
            ) : (
              <>
                <Grid item xs={12} md={3}>
                  <ButtonComponentsUi
                    width="100%"
                    colorType={false}
                    Title={"CAMPS"}
                    cursor="default"
                    count={formatNumberCustomPattern(
                      list.daily?.camp_count ?? 0,
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <ButtonComponentsUi
                    width="100%"
                    colorType={false}
                    Title={"BENEFICIARIES"}
                    cursor="default"
                    count={formatNumberCustomPattern(list.daily?.total ?? 0)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <ButtonComponentsUi
                    width="100%"
                    colorType={false}
                    Title={"NORMAL HEARING"}
                    cursor="default"
                    count={formatNumberCustomPattern(list.daily?.normal ?? 0)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <ButtonComponentsUi
                    width="100%"
                    colorType={false}
                    Title={"HEARING LOSS"}
                    cursor="default"
                    count={formatNumberCustomPattern(
                      list.daily?.hearing_loss ?? 0,
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <HearingLossChart
            hearingLossCounts={list.hearingLossCounts ?? {}}
            totalBeneficiaries={list.overall?.total ?? 0}
            loading={loading}
          />
        </Paper>
      </Box>
    </DashboardContainer>
  );
};

export default DashboardListUi;
